import { actions, persistReducer, localStorage, useIntl, useSelector, useFeatureToggles, ReducersRegistry } from '@grandvisionhq/state';
import * as React from 'react';
import React__default, { useContext } from 'react';
import classnames from 'classnames';
import { Loader, Alert, Tooltip, MediaQuery, Modal, Button, RichTextArea } from '@grandvisionhq/common';
import { Icon } from '@grandvisionhq/icons';
import { gql, useQuery } from '@grandvisionhq/graphql';
import { createSelector } from 'reselect';
import { Heading, Hr } from '@grandvisionhq/elements';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

// TODO: this should probably have a stricter type than string,
//  but the ShippingMethod enum lives in the checkout package currently
var setPreferredShippingMethod = actions.action('shipping-methods/set-preferred-shipping-method', actions.payload());

var initialState = {
    preferredMethodKey: null,
};
var reducer = actions.reducer(initialState, 
/**
 * Storing user's preferred shipping method.
 * Currently this is the last shipping method selected by the user in the checkout/cart
 */
actions.on(setPreferredShippingMethod, function (s, _a) {
    var shippingMethodKey = _a.payload.shippingMethodKey;
    return (__assign(__assign({}, s), { preferredMethodKey: shippingMethodKey }));
}));
var persistConfig = {
    storage: localStorage,
    key: 'grandvision_shipping',
};
var shippingReducer = persistReducer(persistConfig, reducer);

var DeliveryIndicatorContext = React__default.createContext({});
var useDeliveryIndicator = function () { return useContext(DeliveryIndicatorContext); };
function withDeliveryIndicator(Component) {
    var wrappedName = Component.displayName || Component.name || 'Component';
    var WithDeliveryIndicator = function (props) { return (React__default.createElement(DeliveryIndicatorContext.Consumer, null, function (context) { return (React__default.createElement(Component, __assign({}, props, { config: context === null || context === void 0 ? void 0 : context.config }))); })); };
    WithDeliveryIndicator.displayName = "WithDeliveryIndicator(" + wrappedName + ")";
    return WithDeliveryIndicator;
}

var DeliveryIndicatorProvider = function (_a) {
    var children = _a.children, config = _a.config, deliveryInfo = _a.deliveryInfo;
    var value = React.useMemo(function () { return ({
        config: config,
        deliveryInfo: deliveryInfo,
    }); }, [config, deliveryInfo]);
    return (React.createElement(DeliveryIndicatorContext.Provider, { value: value }, children));
};

function getProductDelivery(config, availability) {
    var missingDeliveryDays = config.missingInventoryChannelStrategy.expectedDeliveryDays;
    if (typeof availability === 'undefined') {
        return missingDeliveryDays;
    }
    var availableChannels = availability.channels || {};
    var channels = Object.entries(availableChannels);
    if (!channels.length) {
        return missingDeliveryDays;
    }
    // !!! Platform has no support for multiple channels yet.
    var _a = __read(channels[0], 2), channelId = _a[0], channel = _a[1];
    var channelDeliveryConfig = config.inventoryChannels[channel.key] || config.inventoryChannels[channelId];
    if (!channelDeliveryConfig) {
        console.error("Inventory channel " + (channel.key || channelId) + " is not configured.");
        return missingDeliveryDays;
    }
    if (typeof channel.availableQuantity === 'undefined') {
        return missingDeliveryDays;
    }
    var stockKey = channel.availableQuantity > 0 ? 'onStock' : 'outOfStock';
    return channelDeliveryConfig.expectedDeliveryDays[stockKey];
}

var defaultDeliveryResolver = function (config, availability) {
    if (availability === void 0) { availability = []; }
    return availability.map(function (availability) { return getProductDelivery(config, availability); });
};

function getDeliveryEstimation(deliveryDays, defaultEstimation) {
    if (!deliveryDays.length) {
        return defaultEstimation;
    }
    var slowest = [0, 0]; // [value, index]
    deliveryDays.forEach(function (value, index) {
        // Strip out anything except numbers and -
        var cleanValue = value.replace(/[^\d-]/g, '');
        // If we have a bound value (like 1-3), take the first number.
        var days = Number(cleanValue.split('-')[0]);
        if (days > slowest[0]) {
            slowest = [days, index];
        }
    });
    // Return the string representation.
    return deliveryDays[slowest[1]];
}

var DeliveryIndicator$1 = function (_a) {
    var labels = _a.labels, loading = _a.loading, error = _a.error, _b = _a.view, view = _b === void 0 ? 'full' : _b, deliveryMessage = _a.deliveryMessage, fallbackLabel = _a.fallbackLabel, tooltipSlot = _a.tooltipSlot, className = _a.className, rest = __rest(_a, ["labels", "loading", "error", "view", "deliveryMessage", "fallbackLabel", "tooltipSlot", "className"]);
    if (loading && !error) {
        return (React__default.createElement("div", { className: "delivery-indicator" },
            React__default.createElement(Loader, null)));
    }
    if (error || (view !== 'short' && !deliveryMessage)) {
        return fallbackLabel ? React__default.createElement("div", { className: "delivery-indicator" }, fallbackLabel) : null;
    }
    var messageElement = React__default.createElement("p", { className: "delivery-indicator__message" }, deliveryMessage);
    var tooltipElement = tooltipSlot ? (React__default.createElement("div", { className: "delivery-indicator__tooltip-slot" }, tooltipSlot)) : null;
    var content = null;
    switch (view) {
        case 'short':
            content = (labels === null || labels === void 0 ? void 0 : labels.deliveryIndication) ? (React__default.createElement("span", { className: "delivery-indicator__message", "data-t": "delivery-short" }, labels.deliveryIndication)) : null;
            break;
        case 'text':
            content = (React__default.createElement(React__default.Fragment, null,
                messageElement,
                tooltipElement));
            break;
        case 'icon':
            content = (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Icon, { name: "delivery", className: "delivery-indicator__icon" }),
                messageElement,
                tooltipElement));
            break;
        case 'full':
            content = (React__default.createElement(Alert, { type: "info", icon: "delivery", "data-t": "delivery-alert" },
                messageElement,
                tooltipElement));
            break;
        default:
            throw new Error("Undefined view: " + view);
    }
    return (React__default.createElement("div", { "data-t": rest['data-t'], className: classnames('delivery-indicator', "delivery-indicator--" + view, className) }, content));
};

var DeliveryIndicatorTooltip = function (_a) {
    var message = _a.message, view = _a.view;
    return (React__default.createElement(Tooltip, { id: "delivery-tooltip", className: classnames("delivery-indicator-tooltip--" + view), message: message }));
};

var DeliveryIndicatorBasicContainer = function (_a) {
    var _b = _a.view, view = _b === void 0 ? 'full' : _b, fallbackLabel = _a.fallbackLabel, _c = _a.availability, availability = _c === void 0 ? [] : _c, _d = _a.deliveryResolver, deliveryResolver = _d === void 0 ? defaultDeliveryResolver : _d, rest = __rest(_a, ["view", "fallbackLabel", "availability", "deliveryResolver"]);
    var _e = useIntl(), getLabel = _e.getLabel, getFormattedLabel = _e.getFormattedLabel;
    var config = useDeliveryIndicator().config;
    // Can't render without availability and fallback label
    if ((availability === null || availability === void 0 ? void 0 : availability.length) === 0 && !fallbackLabel) {
        return null;
    }
    var deliveries = deliveryResolver(config, availability);
    var deliveryEstimation = getDeliveryEstimation(deliveries, config.missingInventoryChannelStrategy.expectedDeliveryDays);
    var isNextDayDelivery = deliveryEstimation === '1';
    var labels = {
        deliveryIndication: getFormattedLabel('products.deliveryIndicator.shortDeliveryIndication', {
            labelData: { days: deliveryEstimation },
        }),
    };
    var message = fallbackLabel || '';
    if (availability.length) {
        if (config.nextDayDeliveryTimeLimit && isNextDayDelivery) {
            message = "" + getFormattedLabel('products.deliveryIndicator.deliveryNextDay', {
                labelData: { timeLimit: config.nextDayDeliveryTimeLimit },
            });
        }
        else if (deliveryEstimation) {
            message = "" + getFormattedLabel('products.deliveryIndicator.deliveryIndication', {
                labelData: { days: deliveryEstimation },
            });
        }
    }
    return (React.createElement(DeliveryIndicator$1, __assign({ view: view, labels: labels, deliveryMessage: message, tooltipSlot: React.createElement(DeliveryIndicatorTooltip, { message: getLabel('products.deliveryIndicator.deliveryTooltip'), view: view }) }, rest)));
};

function mapEstimatedDelivery(data, locale) {
    var _a, _b, _c, _d, _e;
    return {
        deliveryDate: ((_a = data === null || data === void 0 ? void 0 : data.estimatedDeliveryBySkus) === null || _a === void 0 ? void 0 : _a.deliveryDate)
            ? new Date(data.estimatedDeliveryBySkus.deliveryDate)
            : undefined,
        shippingMethodDescription: (_e = (_d = (_c = (_b = data === null || data === void 0 ? void 0 : data.estimatedDeliveryBySkus) === null || _b === void 0 ? void 0 : _b.shippingMethodDescription) === null || _c === void 0 ? void 0 : _c.find(function (description) { return description.locale === locale || description.locale === locale.substr(0, 2); })) === null || _d === void 0 ? void 0 : _d.value) !== null && _e !== void 0 ? _e : '',
    };
}

var estimatedDelivery = gql(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  fragment EstimatedDeliveryFragment on EstimatedDelivery {\n    deliveryDate\n    shippingDate\n    shippingMethodDescription {\n      locale\n      value\n      __typename\n    }\n    __typename\n  }\n"], ["\n  fragment EstimatedDeliveryFragment on EstimatedDelivery {\n    deliveryDate\n    shippingDate\n    shippingMethodDescription {\n      locale\n      value\n      __typename\n    }\n    __typename\n  }\n"])));
var templateObject_1$1;

var GET_ESTIMATED_DELIVERY_BY_SKUS = gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  query GetEstimatedDeliveryBySkus($input: EstimatedDeliveryBySkuInput!) {\n    estimatedDeliveryBySkus(input: $input) {\n      ...EstimatedDeliveryFragment\n    }\n  }\n  ", "\n"], ["\n  query GetEstimatedDeliveryBySkus($input: EstimatedDeliveryBySkuInput!) {\n    estimatedDeliveryBySkus(input: $input) {\n      ...EstimatedDeliveryFragment\n    }\n  }\n  ", "\n"])), estimatedDelivery);
var templateObject_1;

var useGetEstimatedDeliveryMessage = function (items, shippingMethodKey) {
    var _a = useIntl(), formatDate = _a.dateTime.formatDate, getFormattedLabel = _a.getFormattedLabel, locale = _a.locale;
    var _b = useQuery(GET_ESTIMATED_DELIVERY_BY_SKUS, {
        variables: {
            input: {
                skuQuantityPairs: items,
                shippingMethodKey: shippingMethodKey,
            },
        },
    }), data = _b.data, loading = _b.loading, error = _b.error;
    var estimatedDelivery = mapEstimatedDelivery(data, locale);
    var deliveryMessage = (estimatedDelivery === null || estimatedDelivery === void 0 ? void 0 : estimatedDelivery.deliveryDate)
        ? getFormattedLabel('products.deliveryIndicator.deliveryIndicationFullDate', {
            labelData: {
                deliveryDate: formatDate(estimatedDelivery.deliveryDate, 'short'),
                deliveryMethod: estimatedDelivery.shippingMethodDescription,
            },
        })
        : '';
    if (error) {
        console.error(error);
    }
    return { deliveryMessage: deliveryMessage, loading: loading, error: error };
};

var useGetDispatchDate = function (items, shippingMethodKey) {
    var _a;
    var _b = useQuery(GET_ESTIMATED_DELIVERY_BY_SKUS, {
        variables: {
            input: {
                skuQuantityPairs: items,
                shippingMethodKey: shippingMethodKey,
            },
        },
        skip: !items || !Array.isArray(items) || !items.length,
    }), data = _b.data, loading = _b.loading, error = _b.error;
    // Dates are returned as any by GraphQL, so we need to cast them
    var dispatchDate = (_a = data === null || data === void 0 ? void 0 : data.estimatedDeliveryBySkus) === null || _a === void 0 ? void 0 : _a.shippingDate;
    if (error) {
        console.error(error);
    }
    return { dispatchDate: dispatchDate, loading: loading, error: error };
};

var getShipping = function (state) { return state.shipping; };

var getPreferredShippingMethod = createSelector(getShipping, function (shipping) { return shipping.preferredMethodKey; });

var DeliveryIndicatorModal = function (_a) {
    var deliveryInfo = _a.deliveryInfo, isModalOpen = _a.isModalOpen, onModalClose = _a.onModalClose;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var _b = deliveryInfo.items, items = _b === void 0 ? [] : _b, description = deliveryInfo.description, link = deliveryInfo.link, linkHint = deliveryInfo.linkHint, title = deliveryInfo.title;
    return (React.createElement(Modal, { className: "delivery-modal", isOpen: isModalOpen, onModalClose: onModalClose, showIcon: true, title: title, footer: link && (React.createElement(React.Fragment, null,
            linkHint && React.createElement("p", null, linkHint),
            React.createElement(Button, { caption: link.label, href: link.url, hrefTarget: link.target === 'external' ? '_blank' : undefined, size: "large" }))) },
        React.createElement("div", { className: "delivery-modal__container" },
            description && (React.createElement(RichTextArea, { className: "delivery-modal__description", content: description })),
            items.map(function (_a) {
                var description = _a.description, id = _a.id, image = _a.image, price = _a.price, title = _a.title;
                return (React.createElement("div", { key: id, className: "delivery-modal-item" },
                    React.createElement("div", { className: "delivery-modal-item__header" },
                        title && (React.createElement(Heading, { component: "h2", variant: "smallest" }, title)),
                        price && (React.createElement(Heading, { component: "h3", variant: "smallest" },
                            "\u00A0\u00B7 ",
                            price))),
                    React.createElement(Hr, { className: "delivery-modal-item__horizontal-rule" }),
                    React.createElement("div", { className: "delivery-modal-item__content" },
                        image && (React.createElement("div", { className: "delivery-modal-item__image-container" },
                            React.createElement("img", { className: "delivery-modal-item__image", src: isSmallScreen ? image.mobile.url : image.original.url, alt: image.alt }))),
                        description && (React.createElement(RichTextArea, { className: "delivery-modal-item__description", content: description })))));
            }))));
};

var DeliveryIndicatorModalToggle = function (_a) {
    var onClick = _a.onClick;
    return (React.createElement("button", { 
        // TODO: create a proper aria-label with BR Label
        // aria-label="tooltip"
        className: "tooltip__button", onClick: onClick },
        React.createElement(Icon, { name: "questionmark" })));
};

var DeliveryIndicatorModalContainer = function () {
    var deliveryInfo = useDeliveryIndicator().deliveryInfo;
    var _a = __read(React.useState(false), 2), isModalOpen = _a[0], setIsModalOpen = _a[1];
    var onModalOpen = React.useCallback(function () {
        setIsModalOpen(true);
    }, []);
    var onModalClose = React.useCallback(function () { return setIsModalOpen(false); }, []);
    if (!deliveryInfo)
        return null;
    return (React.createElement(React.Fragment, null,
        React.createElement(DeliveryIndicatorModalToggle, { onClick: onModalOpen }),
        React.createElement(DeliveryIndicatorModal, { deliveryInfo: deliveryInfo, isModalOpen: isModalOpen, onModalClose: onModalClose })));
};

/**
 * This component shows a full delivery date estimation instead of a basic indication (e.g. 1-3 days)
 * The estimate is calculated on the backend
 */
var DeliveryIndicatorFullDateContainer = function (_a) {
    var _b = _a.items, items = _b === void 0 ? [] : _b, fallbackLabel = _a.fallbackLabel, _c = _a.view, view = _c === void 0 ? 'full' : _c, rest = __rest(_a, ["items", "fallbackLabel", "view"]);
    var preferredShippingMethod = useSelector(getPreferredShippingMethod);
    var _d = useGetEstimatedDeliveryMessage(items, preferredShippingMethod), deliveryMessage = _d.deliveryMessage, loading = _d.loading, error = _d.error;
    return (React__default.createElement(DeliveryIndicator$1, __assign({ deliveryMessage: deliveryMessage, loading: loading, error: !!error, view: view, fallbackLabel: fallbackLabel, tooltipSlot: React__default.createElement(DeliveryIndicatorModalContainer, null) }, rest)));
};

var DeliveryIndicator = function (_a) {
    var _b = _a.items, items = _b === void 0 ? [] : _b, deliveryResolver = _a.deliveryResolver, availability = _a.availability, fallbackLabel = _a.fallbackLabel, rest = __rest(_a, ["items", "deliveryResolver", "availability", "fallbackLabel"]);
    var isDeliveryFullDateEnabled = useFeatureToggles().isDeliveryFullDateEnabled;
    var getLabel = useIntl().getLabel;
    var fallbackLabelValue = fallbackLabel !== null && fallbackLabel !== void 0 ? fallbackLabel : getLabel('products.deliveryIndicator.fallbackMessage');
    // Full date delivery indicator that fetches a calculated delivery estimate from the backend
    if (isDeliveryFullDateEnabled && (items === null || items === void 0 ? void 0 : items.length) > 0) {
        return React__default.createElement(DeliveryIndicatorFullDateContainer, __assign({ items: items, fallbackLabel: fallbackLabelValue }, rest));
    }
    return (
    // Basic delivery indicator that renders delivery estimate based on stock levels
    React__default.createElement(DeliveryIndicatorBasicContainer, __assign({ deliveryResolver: deliveryResolver, availability: availability, fallbackLabel: fallbackLabelValue }, rest)));
};

ReducersRegistry.register('shipping', shippingReducer);

export { DeliveryIndicator, DeliveryIndicatorProvider, defaultDeliveryResolver, getPreferredShippingMethod, setPreferredShippingMethod, useDeliveryIndicator, useGetDispatchDate, useGetEstimatedDeliveryMessage, withDeliveryIndicator };
//# sourceMappingURL=index.js.map
